import { useEffect, useRef, useState } from "react";
import { MdArrowBack } from "react-icons/md";
import { useTranslation } from "react-i18next";
import type { MainColor } from "../../libs/simple/main";
import { applyTheme, buildAiPrompt, parseTheme } from "../../libs/simple/theme";
import InputButton from "../InputButton";
import type { PreviewSession } from "./usePreviewSession";

type AiPanelProps = {
  onClose(): void;
  mains: MainColor[];
  session: PreviewSession;
};

const TEXT_FIELDS = "input, textarea, select, [contenteditable='true']";

// A wrong paste can produce an error per line; the first few tell the story.
const MAX_ERRORS_SHOWN = 10;

// The AI flow, shown in the Simple panel in place of its normal content so
// the map being previewed stays in view. It is only mounted while open:
// every way of closing it (back button, the toolbar button, Escape, leaving
// Simple mode) unmounts it, and unmounting cancels a preview not applied.
export default function AiPanel(props: AiPanelProps) {
  const { t } = useTranslation();
  const { session, mains } = props;
  const [reply, setReply] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [checked, setChecked] = useState(false);
  const promptField = useRef<HTMLTextAreaElement | null>(null);

  // Always the newest props, for the handlers that run late.
  const latest = useRef(props);
  latest.current = props;

  useEffect(() => {
    return () => latest.current.session.cancel();
  }, []);

  // Escape closes the view, except while typing (there it only leaves the
  // field) and while a dialog is open on top (there it closes the dialog).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (e.target instanceof Element && e.target.closest(TEXT_FIELDS)) return;
      if (document.querySelector(".studio-modal")) return;
      latest.current.onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // A preview that ended from outside (undo, redo, another style opened) is
  // no longer on the map, so there is nothing to apply: offer Check again.
  const previewing = checked && session.isPreviewing();

  // Built from the un-previewed style, so copying mid-preview is still right.
  const prompt = buildAiPrompt(session.original(), mains);

  const cancelPreview = () => {
    session.cancel();
    setChecked(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
    } catch (err) {
      // Clipboard blocked: select the text so the user can copy by hand.
      promptField.current?.select();
    }
  };

  const check = () => {
    const result = parseTheme(reply, mains, session.original());
    if (!result.ok) {
      cancelPreview();
      setErrors(result.errors);
      return;
    }
    setErrors([]);
    setChecked(true);
    session.preview((base) => applyTheme(base, mains, result.colors));
  };

  const apply = () => {
    session.commit();
    props.onClose();
  };

  return (
    <div className="studio-ai" data-wd-key="modal:ai">
      <header className="studio-simple__header">
        <button
          type="button"
          className="studio-simple__back"
          aria-label={t("Back to overview")}
          data-wd-key="modal:ai:close"
          onClick={props.onClose}
        >
          <MdArrowBack />
        </button>
        <h2 className="studio-simple__title">{t("Edit with AI")}</h2>
      </header>

      <section className="studio-ai__step">
        <h3>{t("1. Copy this into any AI chat")}</h3>
        <p>{t("Paste this into ChatGPT, Claude or any other AI chat.")}</p>
        <p>
          {t(
            "Before sending, replace the line in angle brackets with the look you want, for example: dark navy night theme."
          )}
        </p>
        <textarea
          ref={promptField}
          className="studio-ai__text"
          data-wd-key="modal:ai:prompt"
          // The prompt and the answer are English text and JSON in every
          // language, so they read left to right.
          dir="ltr"
          aria-label={t("Prompt for the AI")}
          readOnly
          rows={6}
          value={prompt}
        />
        <InputButton onClick={copy} data-wd-key="modal:ai:copy">
          {copied ? t("Copied") : t("Copy prompt")}
        </InputButton>
      </section>

      <section className="studio-ai__step">
        <h3>{t("2. Paste the AI's answer here")}</h3>
        <textarea
          className="studio-ai__text"
          data-wd-key="modal:ai:reply"
          dir="ltr"
          aria-label={t("AI answer")}
          rows={6}
          spellCheck={false}
          value={reply}
          onChange={(e) => {
            setReply(e.target.value);
            // An edited answer is no longer what the map is showing.
            if (previewing) cancelPreview();
          }}
        />
        {/* Always present, so a screen reader announces what appears in it. */}
        <div aria-live="polite">
          {errors.length > 0 && (
            <ul className="studio-ai__errors" data-wd-key="modal:ai:errors">
              {errors.slice(0, MAX_ERRORS_SHOWN).map((error, i) => (
                <li key={i}>{error}</li>
              ))}
              {errors.length > MAX_ERRORS_SHOWN && (
                <li data-wd-key="modal:ai:errors:more">
                  {t("+{{number}} more", {
                    number: errors.length - MAX_ERRORS_SHOWN,
                  })}
                </li>
              )}
            </ul>
          )}
          {previewing && (
            <p className="studio-ai__status" data-wd-key="modal:ai:previewing">
              {t("Previewing on the map.")}
            </p>
          )}
        </div>
        {previewing ? (
          <div className="studio-ai__actions">
            <InputButton
              className="studio-button--tinted"
              onClick={apply}
              data-wd-key="modal:ai:apply"
            >
              {t("Apply")}
            </InputButton>
            <InputButton
              className="studio-button--quiet"
              onClick={cancelPreview}
              data-wd-key="modal:ai:cancel"
            >
              {t("Cancel")}
            </InputButton>
          </div>
        ) : (
          <div className="studio-ai__actions">
            <InputButton
              className="studio-button--tinted"
              onClick={check}
              disabled={reply.trim() === ""}
              data-wd-key="modal:ai:check"
            >
              {t("Check")}
            </InputButton>
          </div>
        )}
      </section>
    </div>
  );
}
